/**
 * TimeSlider — Range 0-120 min with 5 snap points and phase labels
 */

import { TIME_POINTS, PHASE_LABELS, type TimePoint } from '../../../../services/cascadeEngine'

interface TimeSliderProps {
  currentTime: TimePoint
  onTimeChange: (t: TimePoint) => void
  onAnimate: () => void
  isAnimating: boolean
}

export function TimeSlider({ currentTime, onTimeChange, onAnimate, isAnimating }: TimeSliderProps) {
  return (
    <div className="term-box p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-term-textDim font-medium uppercase tracking-wider">
          Cascade Timeline
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-term-text tabular-nums">
            T+{currentTime}min
          </span>
          <span className="text-[10px] px-2 py-0.5 rounded bg-term-bgLight text-term-textDim">
            {PHASE_LABELS[currentTime]}
          </span>
          <button
            onClick={onAnimate}
            disabled={isAnimating}
            className="px-2 py-1 text-[10px] font-bold uppercase rounded
                       bg-term-primary/20 text-term-primary border border-term-primary/40
                       hover:bg-term-primary/30 transition-colors
                       disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isAnimating ? 'Playing...' : 'Animate'}
          </button>
        </div>
      </div>

      {/* Snap-point timeline */}
      <div className="flex items-center gap-0">
        {TIME_POINTS.map((tp, idx) => {
          const isActive = tp === currentTime
          const isPast = tp < currentTime
          return (
            <div key={tp} className="flex items-center flex-1">
              <button
                onClick={() => onTimeChange(tp)}
                className={`flex flex-col items-center gap-1 flex-1 py-1.5 rounded transition-all cursor-pointer
                           ${isActive ? 'bg-term-primary/15' : 'hover:bg-term-bgLight/30'}`}
              >
                <div
                  className={`w-3 h-3 rounded-full border-2 transition-all
                    ${isActive ? 'bg-term-primary border-term-primary scale-125' :
                      isPast ? 'bg-term-primary/50 border-term-primary/50' :
                      'bg-term-bgLight border-term-border'}`}
                />
                <div className={`text-[10px] font-bold tabular-nums ${isActive ? 'text-term-primary' : 'text-term-textDim'}`}>
                  {tp}m
                </div>
                <div className={`text-[8px] uppercase ${isActive ? 'text-term-primary' : 'text-term-textDim/60'}`}>
                  {PHASE_LABELS[tp]}
                </div>
              </button>
              {idx < TIME_POINTS.length - 1 && (
                <div className={`h-[2px] w-full ${isPast || isActive ? 'bg-term-primary/50' : 'bg-term-border'}`} />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
